import { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  apiGetMe,
  apiGetUserRoles,
  apiSignInWithMagicLink,
  apiSignOut,
  setAccessToken,
  clearAccessToken,
} from '../services/api.js';
import { playerSignOut } from '../services/playerAuthClient.js';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const ROLES_CACHE_KEY = 'tennisteam_user_roles_cache';
const ROLES_CACHE_TS_KEY = 'tennisteam_user_roles_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function cacheUserRoles(userId, roles) {
  try {
    localStorage.setItem(ROLES_CACHE_KEY, JSON.stringify({ userId, roles }));
    localStorage.setItem(ROLES_CACHE_TS_KEY, Date.now().toString());
  } catch {}
}

function getCachedUserRoles(userId) {
  try {
    const cached = localStorage.getItem(ROLES_CACHE_KEY);
    const ts = localStorage.getItem(ROLES_CACHE_TS_KEY);
    if (!cached || !ts) return null;
    if (Date.now() - parseInt(ts, 10) > CACHE_DURATION) {
      localStorage.removeItem(ROLES_CACHE_KEY);
      localStorage.removeItem(ROLES_CACHE_TS_KEY);
      return null;
    }
    const { userId: id, roles } = JSON.parse(cached);
    return id === userId ? roles : null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // true when the current session was established via Better Auth (player login)
  const [isPlayerAuth, setIsPlayerAuth] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const refreshTimerRef = useRef(null);

  const fetchAndCacheRoles = async (userId, retryCount = 0) => {
    if (!userId) { setUserRoles([]); return []; }
    const cached = getCachedUserRoles(userId);
    try {
      const roles = await apiGetUserRoles(userId);
      setUserRoles(roles);
      cacheUserRoles(userId, roles);
      return roles;
    } catch (err) {
      console.error(`Error fetching roles (attempt ${retryCount + 1}/3):`, err.message);
      if (retryCount < 2) {
        await new Promise(r => setTimeout(r, Math.min(1000 * Math.pow(2, retryCount), 5000)));
        return fetchAndCacheRoles(userId, retryCount + 1);
      }
      if (cached) { setUserRoles(cached); return cached; }
      return userRoles;
    }
  };

  const hydrateFromToken = async () => {
    try {
      const me = await apiGetMe();
      setUser(me.user);
      setIsPlayerAuth(false);
      await fetchAndCacheRoles(me.user.id);
    } catch {
      clearAccessToken();
      setUser(null);
      setUserRoles([]);
    } finally {
      setLoading(false);
    }
  };

  /** Hydrate from a Better Auth (player) session cookie */
  const hydrateFromPlayerSession = async () => {
    try {
      const res = await fetch('/api/player-auth-ext/me', { credentials: 'include' });
      if (!res.ok) return false;
      const data = await res.json();
      setUser(data.user);
      setUserRoles(data.roles);
      setIsPlayerAuth(true);
      setMustChangePassword(data.player?.mustChangePassword ?? false);
      cacheUserRoles(data.user.id, data.roles);
      return true;
    } catch {
      return false;
    }
  };

  // Exposed so AuthCallback / SetPasswordPage can trigger hydration after login
  const rehydrate = hydrateFromToken;

  /** Called by SetPasswordPage after a successful BA sign-in */
  const rehydratePlayer = async () => {
    const ok = await hydrateFromPlayerSession();
    if (!ok) setLoading(false);
  };

  // On mount: try JWT refresh first, then BA session
  useEffect(() => {
    let mounted = true;
    (async () => {
      // 1. Try JWT refresh token (admin / captain flow)
      try {
        const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
        if (!mounted) return;
        if (res.ok) {
          const data = await res.json();
          setAccessToken(data.access_token);
          await hydrateFromToken();
          return;
        }
      } catch { /* fall through */ }

      // 2. Try Better Auth session (player login flow)
      if (!mounted) return;
      const playerOk = await hydrateFromPlayerSession();
      if (!mounted) return;
      setLoading(false);
    })();

    return () => { mounted = false; };
  }, []);

  // Refresh JWT access token every 50 minutes (JWT sessions only)
  useEffect(() => {
    if (!user || isPlayerAuth) return;
    const id = setInterval(async () => {
      try {
        const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setAccessToken(data.access_token);
        }
      } catch (err) {
        console.error('Token refresh failed:', err);
      }
    }, 50 * 60 * 1000);
    refreshTimerRef.current = id;
    return () => clearInterval(id);
  }, [user, isPlayerAuth]);

  const signInWithMagicLink = async (email) => {
    try {
      setError(null);
      const data = await apiSignInWithMagicLink(email);
      return { data, error: null };
    } catch (err) {
      console.error('Error sending magic link:', err);
      setError(err.message);
      return { data: null, error: err };
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      if (isPlayerAuth) {
        await playerSignOut();
      } else {
        await apiSignOut();
      }
      sessionStorage.removeItem('captainAuth');
      localStorage.removeItem('playerToken');
      localStorage.removeItem(ROLES_CACHE_KEY);
      localStorage.removeItem(ROLES_CACHE_TS_KEY);
      clearAccessToken();
      setUser(null);
      setUserRoles([]);
      setIsPlayerAuth(false);
      setMustChangePassword(false);
      return { error: null };
    } catch (err) {
      console.error('Error signing out:', err);
      clearAccessToken();
      setUser(null);
      setUserRoles([]);
      setIsPlayerAuth(false);
      setMustChangePassword(false);
      return { error: null };
    }
  };

  const refreshRoles = async () => {
    if (!user) return;
    if (isPlayerAuth) {
      await hydrateFromPlayerSession();
    } else {
      await fetchAndCacheRoles(user.id);
    }
  };

  const session = user
    ? { user, access_token: 'managed-by-api-service' }
    : null;

  const value = {
    user,
    session,
    userRoles,
    loading,
    error,
    isPlayerAuth,
    mustChangePassword,
    setMustChangePassword,
    signInWithMagicLink,
    signOut,
    refreshRoles,
    rehydrate,
    rehydratePlayer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
